import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { settingsApi } from '../../api/settings';
import {
  estimatePlotTrackGenerateCostUsd,
  formatGenerateEstimateLine,
  pricingFromCatalog,
  resolveStoryBoardModelId,
} from '../../utils/estimatePlotTrackGenerateCost';
import {
  CANT_ESTIMATE_NOW_LINE,
  ESTIMATE_SLOW_MS,
} from '../../utils/estimateSceneWritingCost';
import { GENERATE_CANT_ESTIMATE } from '../../utils/writerCopy';
import { bandShowsRange } from '../../utils/estimateOutlineCost';
import './PlotTrackGenerateConfirm.css';

/**
 * Confirm before Plot Grid Generate (#1174).
 * Shows a typical-to-worst dollar band from the live prompt size and the
 * writer's Story Board model rates. Cancel generates nothing. The cost
 * is shown once, here. Confirm stays off while the model, the rates,
 * or the prompt size are still loading, and uses the same slow-read
 * line if that wait runs out. A finished miss still does not confirm.
 * This is not a spend cap.
 */
export default function PlotTrackGenerateConfirm({
  fillsSpine = false,
  estimatedInputTokens = null,
  promptChars = null,
  sizePending = false,
  onConfirm,
  onCancel,
}) {
  const [modelId, setModelId] = useState('');
  const [modelName, setModelName] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [ratesState, setRatesState] = useState('loading');
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), ESTIMATE_SLOW_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRatesState('loading');
    (async () => {
      try {
        const [settingsRes, catalogRes] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getOpenRouterCatalog().catch(() => null),
        ]);
        if (cancelled) return;
        const settings = settingsRes?.data || settingsRes || {};
        const id = resolveStoryBoardModelId(settings?.model_preferences);
        setModelId(id);
        const models = catalogRes?.data?.models || catalogRes?.models || [];
        if (!models.length) {
          setCatalog([]);
          setModelName('');
          setRatesState('missing');
          return;
        }
        setCatalog(models);
        const entry = models.find((model) => model.id === id);
        setModelName(entry?.name || id);
        setRatesState('ready');
      } catch {
        if (!cancelled) {
          setCatalog([]);
          setModelName('');
          setRatesState('missing');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pricing = pricingFromCatalog(catalog, modelId);
  const hasSize = estimatedInputTokens != null || promptChars != null;
  const band = ratesState === 'ready' && hasSize
    ? estimatePlotTrackGenerateCostUsd(pricing, {
      fillsSpine,
      estimatedInputTokens,
      promptChars,
    })
    : null;
  const estimateReady = Boolean(band);
  const waitingOnRates = ratesState === 'loading';
  const waitingOnSize = ratesState === 'ready' && !hasSize && sizePending;
  const waiting = waitingOnRates || waitingOnSize;
  let estimateLine = 'Loading estimate…';
  if (waiting && slow) estimateLine = CANT_ESTIMATE_NOW_LINE;
  else if (waiting) estimateLine = 'Loading estimate…';
  else if (estimateReady) estimateLine = formatGenerateEstimateLine(band);
  else estimateLine = GENERATE_CANT_ESTIMATE;
  const confirmDisabled = waiting ? !slow : !estimateReady;
  const modelLabel = modelName
    || ((waitingOnRates && !slow) ? 'Loading model…' : (modelId || 'Story Board model'));

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div className="pt-generate-confirm-backdrop" onClick={handleBackdrop}>
      <div
        className="pt-generate-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pt-generate-confirm-title"
      >
        <h3 id="pt-generate-confirm-title" className="pt-generate-confirm__title">
          Generate Plot Grid
        </h3>
        <p className="pt-generate-confirm__hint">
          {bandShowsRange(band)
            ? 'A typical run costs the lower figure. If Generate has to keep going, it costs the higher. '
            : ''}
          You still accept or reject the outline.
        </p>
        <p className="pt-generate-confirm__model">
          <span className="pt-generate-confirm__label">Model</span>
          <span>{modelLabel}</span>
        </p>
        <p className="pt-generate-confirm__estimate" data-testid="pt-generate-estimate">
          {estimateLine}
        </p>
        <div className="pt-generate-confirm__actions">
          <button
            type="button"
            className="pt-generate-confirm__btn pt-generate-confirm__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pt-generate-confirm__btn pt-generate-confirm__btn--confirm"
            onClick={() => { if (!confirmDisabled) onConfirm?.(); }}
            disabled={confirmDisabled}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
